// btw-front/src/components/layout/Navbar.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { message } from "antd";
import Container from "../ui/Container";
import { Button } from "../ui/Button";
import { useTranslation } from "react-i18next";
import { usePartnerSession } from "../../context/PartnerSessionContext";

// 시안 IA: 단일 페이지 앵커 (Solutions / Use Cases / Process / Results / Contact)
const sections = [
  { id: "services", key: "services" },
  { id: "usecases", key: "usecases" },
  { id: "process", key: "process" },
  { id: "results", key: "results" },
  { id: "contact", key: "contact" },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [isTop, setIsTop] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { role, profile, logout } = usePartnerSession();

  const isLoggedIn = role === "partner" || role === "admin" || role === "pending";
  const homePath =
    role === "admin" ? "/admin" : role === "pending" ? "/partners/pending" : "/partners/dashboard";
  const homeLabel = role === "admin" ? t("nav.admin") : t("nav.dashboard");

  const handleLogout = () => {
    setMobileOpen(false);
    setUserMenuOpen(false);
    logout();
    message.success(t("nav.logoutDone"));
    navigate("/partners", { replace: true });
  };

  // 사용자 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    if (!userMenuOpen) return undefined;
    const onClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [userMenuOpen]);

  // 라우트 전환 시 사용자 메뉴 자동 닫기
  useEffect(() => {
    setUserMenuOpen(false);
  }, [location.pathname]);

  const LOGO_SRC = `${process.env.PUBLIC_URL}/images/btwsoft/logo_b_3.png`;

  useEffect(() => {
    const onScroll = () => setIsTop(window.scrollY < 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // 모바일 메뉴 열렸을 때 스크롤 방지(선택)
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "auto";
    return () => (document.body.style.overflow = "auto");
  }, [mobileOpen]);

  const navItems = useMemo(() => sections.map((s) => ({ ...s, label: t(`nav.${s.key}`) })), [t]);

  const toggleLang = () => i18n.changeLanguage(i18n.language === "ko" ? "en" : "ko");

  const goTo = (id) => {
    setMobileOpen(false);
    // 홈이 아니면 홈으로 이동(hash 포함) — Home.jsx 의 useEffect 가 해당 섹션으로 스크롤
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    if (id === "top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // 로고/홈 이동: 다른 페이지면 라우팅, 홈이면 최상단 스크롤
  const goHome = () => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header
      className={`sticky top-0 z-50 bg-slate-950/95 backdrop-blur transition-shadow duration-300 ${
        isTop ? "" : "border-b border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.25)]"
      }`}
    >
      <Container className="py-3.5">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <button
            type="button"
            onClick={goHome}
            className="group flex items-center gap-3 text-left"
            aria-label="Go to top"
          >
            <img
              src={LOGO_SRC}
              alt="Between Soft logo"
              className="h-10 w-10 rounded-xl object-contain ring-1 ring-white/15 transition group-hover:ring-accent/60"
              loading="eager"
            />
            <div className="text-xl font-bold tracking-tight bg-gradient-to-r from-white to-cyan-200 bg-clip-text text-transparent">
              BetweenSoft
            </div>
          </button>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-7 text-[15px] font-medium text-slate-200">
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => goTo(item.id)}
                className="relative transition-colors hover:text-white after:absolute after:-bottom-1 after:left-0 after:h-0.5 after:w-full after:origin-left after:scale-x-0 after:bg-accent after:transition-transform after:duration-300 hover:after:scale-x-100 motion-reduce:after:hidden"
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            <Button variant="ghost-invert" className="hidden sm:inline-flex" onClick={toggleLang}>
              {i18n.language === "ko" ? "EN" : "KO"}
            </Button>

            {isLoggedIn ? (
              <div ref={userMenuRef} className="relative hidden sm:block">
                <button
                  type="button"
                  onClick={() => setUserMenuOpen((v) => !v)}
                  className="btw-btn-ghost-invert"
                  aria-haspopup="menu"
                  aria-expanded={userMenuOpen}
                >
                  <span aria-hidden="true">👤</span>
                  <span className="max-w-[140px] truncate">{profile?.name || homeLabel}</span>
                  <span aria-hidden="true" className="text-white/60">
                    ▾
                  </span>
                </button>
                {userMenuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 mt-2 w-52 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setUserMenuOpen(false);
                        navigate(homePath);
                      }}
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-700 transition hover:bg-slate-50"
                    >
                      <span aria-hidden="true">🏠</span>
                      <span>{homeLabel}</span>
                    </button>
                    {role === "partner" && (
                      <button
                        type="button"
                        onClick={() => {
                          setUserMenuOpen(false);
                          navigate("/partners/profile");
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-700 transition hover:bg-slate-50"
                      >
                        <span>{t("nav.profile")}</span>
                      </button>
                    )}
                    {role === "admin" && (
                      <button
                        type="button"
                        onClick={() => {
                          setUserMenuOpen(false);
                          navigate("/admin/sales-reps");
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-700 transition hover:bg-slate-50"
                      >
                        <span>{t("nav.adminSalesReps")}</span>
                      </button>
                    )}
                    <div className="my-1 border-t border-slate-100" />
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-rose-600 transition hover:bg-rose-50"
                    >
                      <span aria-hidden="true">⏻</span>
                      <span>{t("nav.logout")}</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Button
                variant="accent"
                onClick={() => {
                  setMobileOpen(false);
                  navigate("/partners");
                }}
                className="hidden sm:inline-flex"
              >
                {t("nav.partner")}
              </Button>
            )}

            {/* Mobile hamburger */}
            <button
              type="button"
              className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/10 backdrop-blur"
              aria-label="Open menu"
              onClick={() => setMobileOpen(true)}
            >
              <span className="sr-only">Open menu</span>
              <div className="grid gap-1">
                <span className="block h-0.5 w-5 bg-white" />
                <span className="block h-0.5 w-5 bg-white" />
                <span className="block h-0.5 w-5 bg-white" />
              </div>
            </button>
          </div>
        </div>
      </Container>

      {/* Mobile menu sheet */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-[60]">
          <button
            type="button"
            className="absolute inset-0 bg-black/30"
            aria-label="Close menu overlay"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute right-0 top-0 h-full w-[86%] max-w-sm bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <img
                  src={LOGO_SRC}
                  alt="Between Soft logo"
                  className="h-8 w-8 rounded-xl object-contain"
                />
                <div className="text-sm font-semibold">Menu</div>
              </div>
              <button
                type="button"
                className="h-10 w-10 rounded-full border border-slate-200"
                aria-label="Close menu"
                onClick={() => setMobileOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="p-4">
              <div className="grid gap-2">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => goTo(item.id)}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-left text-sm text-slate-900"
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <div className="mt-4 grid gap-2">
                <button
                  type="button"
                  onClick={toggleLang}
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium"
                >
                  {i18n.language === "ko" ? "Switch to English" : "한국어로"}
                </button>

                {isLoggedIn ? (
                  <>
                    <Link
                      to={homePath}
                      onClick={() => setMobileOpen(false)}
                      className="w-full rounded-xl bg-accent px-4 py-3 text-center text-sm font-medium text-white"
                    >
                      👤 {profile?.name || homeLabel} · {homeLabel}
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900"
                    >
                      {t("nav.logout")}
                    </button>
                  </>
                ) : (
                  <Link
                    to="/partners"
                    onClick={() => setMobileOpen(false)}
                    className="w-full rounded-xl bg-accent px-4 py-3 text-center text-sm font-medium text-white"
                  >
                    {t("nav.partner")}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
